"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.rgbOpacity = exports.getUniqueFontWeights = exports.pxToRelative = exports.pxToRems = exports.setToLayer = exports.buildResponsiveValueCss = exports.universalBtoa = exports.splitPropertyName = exports.debounce = exports.useWindowWidth = exports.useWindowHeight = exports.useWindowSize = exports.usePrevious = exports.useKeypress = exports.useDisclosure = exports.getPluralString = exports.createIdFromString = exports.getShadowStyle = void 0;
const react_1 = require("react");
const btoa_1 = __importDefault(require("btoa"));
const react_constants_1 = require("./react.constants");
const getShadowStyle = ({ theme, depth = 1, }) => {
    const propertyKey = `EffectElevation${Math.abs(depth)}${depth > 0 ? 'Bottom' : 'Top'}`;
    return `${theme[`${propertyKey}0OffsetX`]}px ${theme[`${propertyKey}0OffsetY`]}px ${theme[`${propertyKey}0Radius`]}px ${theme[`${propertyKey}0Spread`]}px ${theme[`${propertyKey}0Color`]}, ${theme[`${propertyKey}1OffsetX`]}px ${theme[`${propertyKey}1OffsetY`]}px ${theme[`${propertyKey}1Radius`]}px ${theme[`${propertyKey}1Spread`]}px ${theme[`${propertyKey}1Color`]}`;
};
exports.getShadowStyle = getShadowStyle;
function createIdFromString(str) {
    return str.toLowerCase().replace(/ /g, '-');
}
exports.createIdFromString = createIdFromString;
const getPluralString = (number, translationSingular, translationPlural) => (number === 1 ? translationSingular : translationPlural);
exports.getPluralString = getPluralString;
function useDisclosure(isOpenDefault = false) {
    const [isOpen, setIsOpen] = (0, react_1.useState)(isOpenDefault);
    const open = (0, react_1.useCallback)(() => setIsOpen(true), []);
    const close = (0, react_1.useCallback)(() => setIsOpen(false), []);
    const toggle = (0, react_1.useCallback)(() => setIsOpen((state) => !state), []);
    return { isOpen, open, close, toggle };
}
exports.useDisclosure = useDisclosure;
const useKeypress = (key, action) => {
    (0, react_1.useEffect)(() => {
        function onKeyup(e) {
            if (e.key === key)
                action();
        }
        window.addEventListener('keyup', onKeyup);
        return () => window.removeEventListener('keyup', onKeyup);
    }, []);
};
exports.useKeypress = useKeypress;
const usePrevious = (value) => {
    const ref = (0, react_1.useRef)();
    (0, react_1.useEffect)(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};
exports.usePrevious = usePrevious;
function useWindowSize(triggeredBy) {
    const isClient = typeof window === 'object';
    const lastWidth = (0, react_1.useRef)(0);
    const lastHeight = (0, react_1.useRef)(0);
    function getSize() {
        return {
            width: isClient ? window.innerWidth : undefined,
            height: isClient ? window.innerHeight : undefined,
        };
    }
    const [windowSize, setWindowSize] = (0, react_1.useState)(getSize);
    function handleResize() {
        if (!isClient) {
            return false;
        }
        const isTriggeredByWidth = triggeredBy === 'width' && (window === null || window === void 0 ? void 0 : window.innerWidth) !== lastWidth.current;
        const isTriggeredByHeight = triggeredBy === 'height' && (window === null || window === void 0 ? void 0 : window.innerHeight) !== lastHeight.current;
        if (isTriggeredByWidth || isTriggeredByHeight || !triggeredBy) {
            const { width, height } = getSize();
            lastWidth.current = width;
            lastHeight.current = height;
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
    }
    (0, react_1.useEffect)(() => {
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowSize;
}
exports.useWindowSize = useWindowSize;
function useWindowHeight() {
    const [windowHeight, setWindowHeight] = (0, react_1.useState)(undefined);
    (0, react_1.useEffect)(() => {
        function handleResize() {
            if (window.innerHeight !== windowHeight) {
                setWindowHeight(window.innerHeight);
            }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowHeight;
}
exports.useWindowHeight = useWindowHeight;
function useWindowWidth() {
    const [windowWidth, setWindowWidth] = (0, react_1.useState)(undefined);
    (0, react_1.useEffect)(() => {
        function handleResize() {
            if (window.innerWidth !== windowWidth) {
                setWindowWidth(window.innerWidth);
            }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowWidth;
}
exports.useWindowWidth = useWindowWidth;
const debounce = (func, waitFor = 300) => {
    let timeout = null;
    const debounced = (...args) => {
        if (timeout !== null) {
            clearTimeout(timeout);
            timeout = null;
        }
        const promise = new Promise((resolve) => {
            timeout = setTimeout(() => {
                resolve(func(...args));
            }, waitFor);
        });
        return promise;
    };
    return debounced;
};
exports.debounce = debounce;
const splitPropertyName = (name) => {
    const splittedName = name.split(',');
    const subtitle = splittedName.filter((_, index) => index !== 1).join(',');
    const propertyName = splittedName[1];
    return { propertyName, subtitle };
};
exports.splitPropertyName = splitPropertyName;
const universalBtoa = (content) => typeof window !== 'undefined' ? window.btoa(content) : (0, btoa_1.default)(content);
exports.universalBtoa = universalBtoa;
const buildResponsiveValueCss = ({ property, values, evaluate, }) => {
    const valueIsArray = Array.isArray(values);
    const defaultValue = valueIsArray ? evaluate(values[0]) : evaluate(values);
    const breakpointValues = valueIsArray ? values.slice(1) : null;
    const breakpointsCss = valueIsArray
        ? breakpointValues
            .map((value, index) => `
            @media screen and (min-width: ${react_constants_1.breakpoints[index]}px) {
              ${property}: ${evaluate(value)};
            }
          `)
            .join('')
        : '';
    return `
    ${property}: ${defaultValue};

    ${breakpointsCss}
  `;
};
exports.buildResponsiveValueCss = buildResponsiveValueCss;
const setToLayer = (layer) => `z-index: ${react_constants_1.LAYERS[layer]};`;
exports.setToLayer = setToLayer;
const pxToRems = (px) => {
    const numPx = typeof px === 'string' ? parseInt(px) : px;
    return `${numPx / 16}rem`;
};
exports.pxToRems = pxToRems;
const pxToRelative = (px) => {
    const numPx = typeof px === 'string' ? parseInt(px) : px;
    return numPx / 16;
};
exports.pxToRelative = pxToRelative;
const getUniqueFontWeights = (theme) => {
    return Array.from(new Set(Object.keys(theme)
        .filter((propertyName) => propertyName.includes('FontTextText') &&
        propertyName.includes('Weight'))
        .map((propertyName) => theme[propertyName])))
        .sort((n1, n2) => n1 - n2)
        .join(';');
};
exports.getUniqueFontWeights = getUniqueFontWeights;
/**
 * Adds or changes opacity value for CSS rgb/rgba color syntax
 * @param {string} rgb - CSS rgb/rgba rgb(12, 34, 56) or rgba(12, 34, 56, 0.7)
 * @param {string|number} opacity - New opacity value <= 1
 * @returns {string} Returns CSS rgba syntax with added or changed opacity value
 */
const rgbOpacity = (rgb, opacity) => {
    return rgb === null || rgb === void 0 ? void 0 : rgb.replace(/rgba?\((\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*)((?:,\s*[0-9.]*\s*)?)\)/, `rgba($1, ${opacity})`);
};
exports.rgbOpacity = rgbOpacity;
//# sourceMappingURL=react.js.map